// posthog-js is ~160KB. To keep it off the first-paint bundle we load it
// lazily (initPostHog dynamically imports it after mount) while still exporting
// a synchronous `posthog` object. Calls made before the library resolves are
// buffered and flushed on load, so capture/identify/reset behavior is unchanged.
type PostHogClient = import("posthog-js").PostHog;

let client: PostHogClient | null = null;
const queue: Array<(p: PostHogClient) => void> = [];

const flush = () => {
  if (!client) return;
  for (const fn of queue.splice(0)) fn(client);
};

// A tiny proxy so call sites keep `posthog.capture(...)` etc. synchronous.
// Methods are fire-and-forget; pre-init calls queue, then replay on load.
export const posthog = new Proxy({} as PostHogClient, {
  get(_target, prop: string) {
    return (...args: unknown[]) => {
      const run = (p: PostHogClient) =>
        (p as unknown as Record<string, (...a: unknown[]) => unknown>)[prop]?.(
          ...args,
        );
      if (client) return run(client);
      queue.push(run);
      return undefined;
    };
  },
});

// Resolves once initPostHog completes. Never resolves when analytics is
// disabled (missing config) or the import fails — race with a timeout.
let readyResolve: ((p: PostHogClient) => void) | null = null;
const ready = new Promise<PostHogClient>((resolve) => {
  readyResolve = resolve;
});
export const whenPostHogReady = () => ready;

// Initialize PostHog (dynamically imports the library off the critical path).
export const initPostHog = async () => {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  const host = import.meta.env.VITE_POSTHOG_HOST;

  if (typeof window === "undefined") return;

  if (!key || !host) {
    console.warn(
      "[PostHog] Analytics disabled - missing configuration.",
      import.meta.env.DEV
        ? {
            VITE_POSTHOG_KEY: key ? "set" : "missing",
            VITE_POSTHOG_HOST: host ? "set" : "missing",
            mode: import.meta.env.MODE,
          }
        : "",
    );
    return;
  }

  try {
    const { default: ph } = await import("posthog-js");
    // One-time migration: drop the legacy host-only cookie (written before
    // cross_subdomain_cookie was enabled) so the shared .specstory.com cookie
    // is the only one PostHog reads. Our state lives in localStorage, and
    // identify() re-runs every signed-in session, so nothing is lost.
    document.cookie = `ph_${key}_posthog=; max-age=0; path=/`;
    ph.init(key, {
      api_host: host,
      person_profiles: "identified_only",
      // usePostHogPageView captures pageviews manually on every route change;
      // leaving the automatic capture on double-counted each view (and, with
      // the pre-identify reset race, under two different distinct_ids).
      capture_pageview: false,
      capture_pageleave: true,
      // Share the $device_id cookie across *.specstory.com so a signed-in
      // cloud user is the same PostHog person on the marketing site
      // (specstory.com) and vice versa.
      persistence: "localStorage+cookie",
      cross_subdomain_cookie: true,
      loaded: (loaded) => {
        console.log("[PostHog] Analytics initialized successfully");
        if (import.meta.env.DEV) {
          loaded.debug();
        }
      },
    });
    client = ph;
    flush();
    readyResolve?.(ph);
  } catch (error) {
    console.error("[PostHog] Failed to initialize:", error);
  }
};
